import * as z from "zod";

import {
  canvasFrameSchema,
  canvasItemSchema,
  CanvasSetUnreadableError,
} from "./document";
import { migrateCanvasSet } from "./migrations";

const currentPartSchemas = {
  frames: z.array(canvasFrameSchema),
  items: z.array(canvasItemSchema),
};

// A v2 arrow `{from, fromBlock, to}` also passes the v3 edge schema, which would drop `fromBlock` silently.
const v2OnlyItemSchema = z.object({ fromBlock: z.string() });

/**
 * A selection copied into chat before v3 carries frames and items in the old
 * shape; each is upgraded as a stored set would be, so old messages still show
 * a chip rather than the raw snapshot text.
 */
function upgradeLegacy(part: "frames" | "items") {
  return (value: unknown): unknown => {
    const isV2 = z
      .array(z.unknown())
      .safeParse(value)
      .data?.some((entry) => v2OnlyItemSchema.safeParse(entry).success);
    if (!isV2 && currentPartSchemas[part].safeParse(value).success) {
      return value;
    }
    try {
      const { set } = migrateCanvasSet({
        schemaVersion: 2,
        name: "",
        brandPath: null,
        thumbnailFrameId: null,
        thumbnail: null,
        frames: part === "frames" ? value : [],
        items: part === "items" ? value : [],
      });
      return set[part];
    } catch (error) {
      // Not an older snapshot either; the schema below reports what is wrong with it.
      if (error instanceof CanvasSetUnreadableError) {
        return value;
      }
      throw error;
    }
  };
}

export const canvasSelectionPictureSchema = z
  .object({
    frameId: z.string(),
    fileId: z.uuid(),
    /** Signed and short-lived; `fileId` is the durable handle. */
    url: z.string(),
    mime: z.string(),
    filename: z.string(),
    sizeBytes: z.number().int().nonnegative(),
  })
  .describe("CanvasSelectionPicture");

export const canvasSelectionMentionSchema = z
  .object({
    kind: z.literal("canvas-selection"),
    frames: z.preprocess(upgradeLegacy("frames"), z.array(canvasFrameSchema)),
    layerIds: z.array(z.string()).default([]),
    /** Quiz frames (`step`, as HTML), the arrows between them (`edge`) and the campaign they sit in (`section`). */
    items: z
      .preprocess(upgradeLegacy("items"), z.array(canvasItemSchema))
      .default([]),
    pictures: z
      .array(canvasSelectionPictureSchema)
      .optional()
      .describe(
        "Rendered pictures of the selected frames, uploaded as chat files, so the model sees each frame as well as its layer data.",
      ),
    source: z
      .object({
        fileId: z.uuid(),
        projectId: z.uuid(),
        name: z.string(),
        fileName: z
          .string()
          .describe(
            "Exact `<title>.canvas.json` filename; what the canvas tools take as the set identifier.",
          ),
      })
      .optional(),
  })
  .describe("CanvasSelectionMention");
